import { useEffect, useMemo, useRef } from "react";

/**
 * Employee count + status counts (Active / OnLeave) for the Employees page.
 *
 * These used to be fetched from /employees/clients/count,
 * /employees/clients/Active/count and /employees/clients/OnLeave/count. Those
 * endpoints aggregate every employee row grouped by client and know nothing
 * about the caller's admin level or manager hierarchy — the only filtering
 * that ever happened was a client-side "keep the row whose name matches my
 * clientName". So the numbers they produced were the whole client, every
 * working company in it, no matter how narrow the caller's visibility was.
 *
 * The cards on this page had already stopped trusting them (they render
 * `calculatedStats`, computed from the visible list), but the values were
 * still being pushed up to Manager_Account via onCountsUpdate /
 * onEmployeeCountUpdate and shown on the dashboard there.
 *
 * Everything is now derived from `employees` — the list this page renders,
 * which the server already filtered to what this caller may see — so the
 * dashboard and the cards agree, and three requests are dropped from load.
 *
 * `fetchEmployeeCount` / `fetchEmployeeCounts` are kept as no-ops so the
 * mutation handlers in useEmployeeActions can keep calling them; the counts
 * recompute on their own as soon as `employees` changes.
 */
export const useEmployeeStats = ({ employees, onCountsUpdate, onEmployeeCountUpdate }) => {
  const employeeList = useMemo(
    () => (Array.isArray(employees) ? employees : []),
    [employees],
  );

  const counts = useMemo(() => {
    let Active = 0;
    let OnLeave = 0;

    employeeList.forEach((emp) => {
      const status = (emp?.status || emp?.employeeState || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      if (status === "active") Active++;
      else if (status === "onleave") OnLeave++;
    });

    return { Active, OnLeave, Recent: 0, Total: employeeList.length };
  }, [employeeList]);

  const employeeCount = counts.Total;

  // Keep the parent (Manager_Account's dashboard) in sync. Refs so a caller
  // passing an inline arrow function doesn't retrigger this every render.
  const onCountsUpdateRef = useRef(onCountsUpdate);
  const onEmployeeCountUpdateRef = useRef(onEmployeeCountUpdate);
  onCountsUpdateRef.current = onCountsUpdate;
  onEmployeeCountUpdateRef.current = onEmployeeCountUpdate;

  useEffect(() => {
    if (typeof onCountsUpdateRef.current === "function") {
      onCountsUpdateRef.current(counts);
    }
    if (typeof onEmployeeCountUpdateRef.current === "function") {
      onEmployeeCountUpdateRef.current(employeeCount);
    }
  }, [counts, employeeCount]);

  const noop = () => {};

  return {
    employeeCount,
    counts,
    fetchEmployeeCount: noop,
    fetchEmployeeCounts: noop,
  };
};
